// UC61-02/03 — Administrator Sistem manages the observer role -> scope map.
// Each row binds an observer role code to the medicine classification tags and
// registrar kinds its institution may see; new role codes can be added without
// a code change (UC61-03). Backed by /api/v1/admin/observer-roles.
//
// The sidebar entry for this screen was folded into Gestiune tablouri, so the
// body renders as a panel inside that screen's third tab. Binding a role to the
// dashboards it may see is NOT done here: that is the role-first editor on the
// `Pe rol` tab (docs/ui-shape-role-first-dashboards.md), and one screen owning
// the association is the whole point. The standalone page stays only as the old
// route's target until it is retired.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudTextInput,
  MudModal,
  MudInfoBox,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import { isObserverRole } from '@simsm/shared-types';
import { apiClient, apiErrorKey } from '../../app/api-client';
import {
  EmptyRegisterMessage,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  useToast,
} from '@simsm/ui-shared';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { registrarKindLabel } from '../../features/registrars/kind-label';

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
}

interface ObserverRole {
  roleCode: string;
  displayName: string;
  description: string;
  medicineTags: string[];
  registrarKinds: string[];
  active: boolean;
  createdAtIso: string;
  updatedAtIso: string;
}

interface FormState {
  roleCode: string;
  displayName: string;
  description: string;
  medicineTagsCsv: string;
  registrarKindsCsv: string;
  // Not edited here — the list owns the toggle — but carried so an edit sends
  // back the flag it found instead of re-enabling a role somebody switched off.
  active: boolean;
}

function emptyForm(): FormState {
  return {
    roleCode: 'OBSERVER_',
    displayName: '',
    description: '',
    medicineTagsCsv: '',
    registrarKindsCsv: '',
    active: true,
  };
}

function roleToForm(role: ObserverRole): FormState {
  return {
    roleCode: role.roleCode,
    displayName: role.displayName,
    description: role.description,
    medicineTagsCsv: role.medicineTags.join(', '),
    registrarKindsCsv: role.registrarKinds.join(', '),
    active: role.active,
  };
}

function splitCsv(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function ObserverRolesPage(): JSX.Element {
  return <ObserverRolesPanel standalone />;
}

export function ObserverRolesPanel({ standalone }: { standalone?: boolean } = {}): JSX.Element {
  const { t } = useTranslate();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formSubmitted, setFormSubmitted] = useState(false);
  // A Tolgee key, never a server sentence: the refusal is read in the
  // administrator's language, and an unknown code still names this operation.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const roles = useQuery({
    queryKey: ['admin-observer-roles'],
    queryFn: () =>
      apiClient.get<{ roles: ObserverRole[] }>('/admin/observer-roles').then((r) => r.data.roles),
  });

  const save = useMutation({
    mutationFn: (payload: Partial<ObserverRole>) =>
      editingCode
        ? apiClient
            .patch<ObserverRole>(
              `/admin/observer-roles/${encodeURIComponent(editingCode)}`,
              payload,
            )
            .then((r) => r.data)
        : apiClient.post<ObserverRole>('/admin/observer-roles', payload).then((r) => r.data),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['admin-observer-roles'] });
      // The editor closes over a table whose row may not even be on screen; the
      // modal disappearing is not the same as the write having landed.
      toast({
        variant: 'success',
        title: editingCode ? t('observer_roles.toast.saved') : t('observer_roles.toast.created'),
        text: saved.displayName || saved.roleCode,
      });
      setShowModal(false);
      setEditingCode(null);
    },
    onError: (e: unknown) => setErrorKey(apiErrorKey(e, 'observer_roles.error.save_failed')),
  });

  const toggleActive = useMutation({
    mutationFn: ({ roleCode, active }: { roleCode: string; active: boolean; name: string }) =>
      apiClient.patch(`/admin/observer-roles/${encodeURIComponent(roleCode)}`, { active }),
    // This write had no `onError` either: a refusal put the button back and said
    // nothing, which is indistinguishable from a click that never fired.
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-observer-roles'] });
      toast({
        variant: variables.active ? 'success' : 'info',
        title: variables.active
          ? t('observer_roles.toast.activated')
          : t('observer_roles.toast.deactivated'),
        text: variables.name,
      });
    },
    onError: (e: unknown, variables) =>
      toast({
        variant: 'error',
        title: t(
          apiErrorKey(
            e,
            variables.active
              ? 'observer_roles.error.activate_failed'
              : 'observer_roles.error.deactivate_failed',
          ),
        ),
        text: variables.name,
      }),
  });

  const rows = roles.data ?? [];
  const columns: TableColumn[] = [
    { key: 'role', label: t('observer_roles.column.role') },
    { key: 'medicineTags', label: t('observer_roles.column.medicine_tags') },
    { key: 'registrarKinds', label: t('observer_roles.column.registrar_kinds') },
    { key: 'status', label: t('observer_roles.column.status') },
    { key: 'actions', label: t('observer_roles.column.actions'), align: 'end' },
  ];

  function openCreate(): void {
    setEditingCode(null);
    setForm(emptyForm());
    setFormSubmitted(false);
    setErrorKey(null);
    setShowModal(true);
  }

  function openEdit(role: ObserverRole): void {
    setEditingCode(role.roleCode);
    setForm(roleToForm(role));
    setFormSubmitted(false);
    setErrorKey(null);
    setShowModal(true);
  }

  function closeModal(): void {
    setShowModal(false);
    setErrorKey(null);
  }

  // The shape rule is @simsm/shared-types' OBSERVER_ROLE_PATTERN and nothing
  // else: a fourth copy of the regex here is how the form came to refuse codes
  // the caiet allows (spec 001 FR-008).
  const roleCodeInvalid =
    !editingCode && !isObserverRole(form.roleCode.trim().toUpperCase())
      ? t('observer_roles.form.error.role_code')
      : null;
  const displayNameInvalid =
    form.displayName.trim().length === 0 ? t('observer_roles.form.error.display_name') : null;

  function submit(): void {
    setFormSubmitted(true);
    if (roleCodeInvalid || displayNameInvalid) return;
    setErrorKey(null);
    save.mutate({
      roleCode: form.roleCode.trim().toUpperCase(),
      displayName: form.displayName.trim(),
      description: form.description.trim(),
      medicineTags: splitCsv(form.medicineTagsCsv),
      registrarKinds: splitCsv(form.registrarKindsCsv),
      active: form.active,
    });
  }

  const createButton = (
    <MudButton shape="circular" variant="primary" size="md" onClick={openCreate}>
      <MudIcon slot="icon-start" name="plus-large" size={16} />
      {t('observer_roles.action.create')}
    </MudButton>
  );

  return (
    <section className="simsm-page simsm-observer-roles">
      {standalone ? (
        <PageHeader
          title={t('observer_roles.title')}
          lede={t('observer_roles.lede')}
          actions={createButton}
        />
      ) : (
        <div className="simsm-dashboards-admin__panel-head">
          <p className="simsm-dashboards-admin__panel-lede">{t('observer_roles.lede')}</p>
          {createButton}
        </div>
      )}

      {roles.isError ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {t('observer_roles.error.load_failed')}
        </MudInfoBox>
      ) : (
        <div className="simsm-observer-roles__table">
          <MudTable
            className="simsm-table"
            columns={columns}
            rows={rows.map((role) => ({
              id: role.roleCode,
              role: role.displayName || role.roleCode,
              medicineTags: role.medicineTags.join(', ') || t('observer_roles.national'),
              registrarKinds:
                role.registrarKinds.map((kind) => registrarKindLabel(t, kind)).join(', ') || '-',
              status: role.active
                ? t('observer_roles.status.active')
                : t('observer_roles.status.inactive'),
              actions: '',
            }))}
            rowStyle="divided"
            disableSort={true}
            aria-label={t('observer_roles.table.aria_label')}
            // The whole row opens the role, the row's first button.
            hoverable
            onMudRowClick={onRowOpen((index) => {
              const role = rows[index];
              if (role) openEdit(role);
            })}
          >
            <EmptyRegisterMessage>{t('observer_roles.empty')}</EmptyRegisterMessage>
            <span slot="header-cell-actions" className="visually-hidden">
              {t('observer_roles.column.actions')}
            </span>
            {rows.map((role, index) => [
              <span
                key={`role-${role.roleCode}`}
                slot={`cell-role-${index}`}
                className="simsm-observer-roles__cell-name"
              >
                <strong>{role.displayName}</strong>
                <code className="simsm-observer-roles__cell-code">{role.roleCode}</code>
              </span>,
              <span
                key={`tags-${role.roleCode}`}
                slot={`cell-medicineTags-${index}`}
                className="simsm-observer-roles__cell-tags"
              >
                {role.medicineTags.length > 0 ? (
                  role.medicineTags.map((tag) => (
                    <span key={tag} className="simsm-observer-roles__cell-tag">
                      <MudTag variant="info" label={tag} />
                    </span>
                  ))
                ) : (
                  <span className="simsm-observer-roles__cell-muted">
                    {t('observer_roles.national')}
                  </span>
                )}
              </span>,
              <span
                key={`kinds-${role.roleCode}`}
                slot={`cell-registrarKinds-${index}`}
                className="simsm-observer-roles__cell-tags"
              >
                {role.registrarKinds.length > 0 ? (
                  role.registrarKinds.map((kind) => (
                    <span key={kind} className="simsm-observer-roles__cell-tag">
                      <MudTag variant="info" label={registrarKindLabel(t, kind)} />
                    </span>
                  ))
                ) : (
                  <span className="simsm-observer-roles__cell-muted">-</span>
                )}
              </span>,
              <span key={`status-${role.roleCode}`} slot={`cell-status-${index}`}>
                <MudTag
                  variant="status"
                  semantic={role.active ? 'success' : 'neutral'}
                  label={
                    role.active
                      ? t('observer_roles.status.active')
                      : t('observer_roles.status.inactive')
                  }
                />
              </span>,
              <span
                key={`actions-${role.roleCode}`}
                slot={`cell-actions-${index}`}
                className="simsm-observer-roles__cell-actions"
              >
                <MudButton
                  shape="circular"
                  variant="neutral"
                  size="sm"
                  onClick={() => openEdit(role)}
                >
                  {t('observer_roles.action.edit')}
                </MudButton>
                <MudButton
                  shape="circular"
                  variant="neutral"
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/system-admin/dashboards-admin?section=by-role&role=${encodeURIComponent(role.roleCode)}`,
                    )
                  }
                >
                  {t('observer_roles.action.dashboards')}
                </MudButton>
                <MudButton
                  shape="circular"
                  variant="neutral"
                  size="sm"
                  disabled={toggleActive.isPending}
                  onClick={async () => {
                    if (role.active) {
                      const ok = await confirm({
                        title: t('confirm.deactivate.title'),
                        body: t('confirm.observer_role.body', { name: role.displayName }),
                        confirmLabel: t('confirm.action.deactivate'),
                        destructive: true,
                      });
                      if (!ok) return;
                    }
                    toggleActive.mutate({
                      roleCode: role.roleCode,
                      active: !role.active,
                      name: role.displayName || role.roleCode,
                    });
                  }}
                >
                  {role.active
                    ? t('observer_roles.action.deactivate')
                    : t('observer_roles.action.activate')}
                </MudButton>
              </span>,
            ])}
          </MudTable>
        </div>
      )}

      <MudModal
        closeLabel={t('common.close')}
        open={showModal}
        size="md"
        titleText={
          editingCode
            ? t('observer_roles.form.edit_title', { code: editingCode })
            : t('observer_roles.form.create_title')
        }
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(closeModal)}
      >
        <div className="simsm-observer-roles__form">
          {errorKey ? (
            <MudInfoBox emphasis="strong" role="alert" variant="error">
              {t(errorKey)}
            </MudInfoBox>
          ) : null}
          {/* The fields carry their own labels, hints and errors: a light-DOM
              <label> around a mud-text-input reaches no control (the native input
              lives in the shadow root), and mud-text-input's own assistive line is
              what the wireframe's `.assistive` text is. */}
          <MudTextInput
            size="md"
            label={t('observer_roles.form.role_code')}
            value={form.roleCode}
            disabled={Boolean(editingCode)}
            placeholder="OBSERVER_XYZ"
            invalid={formSubmitted && roleCodeInvalid !== null}
            errorText={roleCodeInvalid ?? ''}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, roleCode: e.detail.value })
            }
          />
          <MudTextInput
            size="md"
            label={t('observer_roles.form.display_name')}
            value={form.displayName}
            invalid={formSubmitted && displayNameInvalid !== null}
            errorText={displayNameInvalid ?? ''}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, displayName: e.detail.value })
            }
          />
          <MudTextInput
            size="md"
            label={t('observer_roles.form.description')}
            value={form.description}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, description: e.detail.value })
            }
          />
          <MudTextInput
            size="md"
            label={t('observer_roles.form.medicine_tags')}
            value={form.medicineTagsCsv}
            placeholder="compensat, epidemiologie"
            helperText={t('observer_roles.form.medicine_tags_hint')}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, medicineTagsCsv: e.detail.value })
            }
          />
          <MudTextInput
            size="md"
            label={t('observer_roles.form.registrar_kinds')}
            value={form.registrarKindsCsv}
            placeholder="IMSP"
            helperText={t('observer_roles.form.registrar_kinds_hint')}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, registrarKindsCsv: e.detail.value })
            }
          />
        </div>
        <div slot="actions" className="simsm-observer-roles__modal-actions">
          <MudButton shape="circular" variant="neutral" size="md" onClick={closeModal}>
            {t('observer_roles.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={save.isPending}
            onClick={submit}
          >
            {t('observer_roles.action.save')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
